import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiFailure,
  api,
  type AdminRecording,
  type RecordingAudio,
  type Version,
  type VersionDetail,
  type VersionList,
  type VersionRow,
} from '../api.ts';
import { formatWhen } from '../duration.ts';
import { ConfirmPromote } from '../ConfirmPromote.tsx';
import {
  needsReview,
  newestSubmitted,
  submittedNote,
  submittedTitle,
  useReviewFacts,
} from '../review.ts';
import { WaveformUnavailable, drawTimeline, loadPeaks, type Peaks } from '../waveform.ts';

/**
 * ONE SOUND, SEVERAL CLAIMS ABOUT IT, ON ONE AXIS.
 *
 * The owner's ask, in his words: "I want to be able to hear and see the same
 * for 2 or 3 versions clips in parallel … at each point what is the word or
 * silence is highlighted for each version."
 *
 * So: the ORIGINAL audio plays — not any version's clips — and every selected
 * arrangement is laid out underneath it as coloured blocks along the SAME time
 * axis. As the playhead moves, each lane lights the block it is inside and says
 * what that version calls this instant. Two versions of the same second, side
 * by side, is the whole product of this screen.
 *
 * WHY THE ORIGINAL AND NOT THE CLIPS. A clip is a timestamp into the recording,
 * not a file of its own; playing a version's clips would make what you HEAR
 * depend on which version you were LOOKING at, and the comparison only means
 * something if the sound is the fixed thing. Hence `GET
 * /admin/recordings/:id/audio` — one signature over the upload, signed longer
 * than a clip URL because this element is held for as long as somebody listens.
 *
 * THE VERSIONS PAGE IS NOT THIS PAGE. It lists rows and lets you read them in
 * order, which is the right shape for checking spellings. Neither replaces the
 * other: rows are for reading, this is for the question "who cut it where".
 *
 * ─────────────────────────────────────────────────────────────────────
 * THREE THINGS THAT WOULD OTHERWISE BE WRITTEN THE OBVIOUS, WRONG WAY:
 *
 * 1. THE PLAYHEAD DOES NOT GO THROUGH REACT. It moves sixty times a second and
 *    the lanes below it hold up to several hundred blocks each; a `setState`
 *    per frame would re-reconcile all of them for a line that moved four
 *    pixels. The frame loop writes one `transform`, one `textContent` per lane
 *    and one class swap, straight to the DOM. React owns everything that
 *    changes when a PERSON does something; the clock owns the rest.
 *
 * 2. THE BLOCKS ARE NOT BUTTONS AND HAVE NO LISTENERS. Three lanes of three
 *    hundred clips is nine hundred elements — nine hundred tab stops and nine
 *    hundred handlers for one click. Instead each LANE has one handler and hit
 *    tests by TIME: the x of the pointer is a timestamp, and the same lookup
 *    that highlights during playback answers "which block is this?". It is
 *    also the only way clicking a two-pixel clip works at all.
 *
 * 3. THE LOOKUP IS NOT A SCAN. Each lane keeps a cursor and checks the block it
 *    was in, then the one after it — which is the answer on almost every frame
 *    — and falls back to a binary search over the starts when the playhead
 *    jumps. Re-scanning from zero per lane per frame is the version of this
 *    that works fine on a 44-second demo and stutters on a real session.
 */

/* ------------------------------------------------------------------ */
/* Small pure things                                                   */
/* ------------------------------------------------------------------ */

/** How many lanes may be shown at once. Three columns of a 1080px page is
 *  already narrow per lane; a fourth turns the labels into confetti. */
const MAX_LANES = 3;

/** Re-ask for a signed URL when it has this long left. A seek that lands past
 *  the buffered edge on a dead signature is a silent failure otherwise. */
const REFRESH_BEFORE_EXPIRY_MS = 60_000;

/** Don't answer a storm of media errors with a storm of requests. */
const MIN_REFRESH_GAP_MS = 5_000;

/** What ← and → move. Two seconds is about one clause of speech. */
const NUDGE_SEC = 2;

/**
 * WAITING FOR THE FIRST RUN, because an upload now ends on this page.
 *
 * A recording opened seconds after it was uploaded has no arrangement yet, and
 * a screen that says so once and then sits there makes a person reload to find
 * out whether anything happened. So it asks again — but a bounded number of
 * times: five minutes of five-second polls. A run that has not produced a
 * version by then has almost certainly failed, and the Recordings tab is where
 * a failure is actually described. An unbounded poll would leave a forgotten
 * tab asking the server about a dead recording until the laptop is closed.
 */
const WAIT_MS = 5000;
const WAIT_TRIES = 60;

/** `mm:ss.t`, tabular, so the digits do not dance while it runs. */
function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00.0';
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${String(m).padStart(2, '0')}:${s.toFixed(1).padStart(4, '0')}`;
}

function span(startSec: number, endSec: number): string {
  return `${clock(startSec)} – ${clock(endSec)} · ${(endSec - startSec).toFixed(2)}s`;
}

/**
 * Which block covers `t`, given where we were last time.
 *
 * `hint` and `hint + 1` first: playback advances by one block at a time, so
 * that is the answer for all but a handful of frames. Everything else — a
 * click, a drag, a jump back — takes the binary search, which is eight
 * comparisons on three hundred clips.
 *
 * Returns -1 when nothing covers `t`. That should not happen inside a version
 * (its active leaves tile the recording exactly) but it does happen at `t`
 * equal to the very end, and a lane that draws nothing is better than one that
 * lights the wrong row.
 */
function blockAt(starts: Float64Array, ends: Float64Array, t: number, hint: number): number {
  const n = starts.length;
  if (n === 0) return -1;
  if (hint >= 0 && hint < n && t >= starts[hint]! && t < ends[hint]!) return hint;
  const next = hint + 1;
  if (next >= 0 && next < n && t >= starts[next]! && t < ends[next]!) return next;

  let lo = 0;
  let hi = n - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid]! <= t) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (found < 0) return -1;
  return t < ends[found]! ? found : -1;
}

type Detail = { at: 'loading' } | { at: 'ready'; detail: VersionDetail } | { at: 'failed'; message: string };

/** One lane's rows plus the two arrays the frame loop searches. */
interface LaneIndex {
  rows: VersionRow[];
  starts: Float64Array;
  ends: Float64Array;
  /**
   * The distinct speakers on this version, in first-heard order — which is the
   * order the sub-lanes stack in, so the person who speaks first is on top.
   *
   * Empty for every version an engine that does not separate speakers wrote,
   * and that is what keeps this change free: no speakers means no bands, and
   * the strip renders exactly as it always did.
   */
  speakers: string[];
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function Player({
  token,
  projectId,
  recordingId,
  initialLanes = '',
  onRecording,
  onSignedOut,
}: {
  token: string;
  projectId: string;
  /** From the hash — `#/player?recording=…` — so a lane comparison can be
   *  bookmarked and sent to somebody. */
  recordingId: string;
  /** From the versions modal: a version id to open alone, or 'all' for the
   *  first few together. '' = the page's own default pick. */
  initialLanes?: string;
  onRecording: (id: string) => void;
  onSignedOut: () => void;
}): JSX.Element {
  const [recordings, setRecordings] = useState<AdminRecording[] | null>(null);
  /** Bumped after "run the first pass", so the status chip and panel follow. */
  const [recBump] = useState(0);
  const [list, setList] = useState<VersionList | null>(null);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [audio, setAudio] = useState<RecordingAudio | null>(null);
  const [peaks, setPeaks] = useState<Peaks | null>(null);
  const [waveNote, setWaveNote] = useState<string | null>(null);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [inspect, setInspect] = useState<{ versionId: string; row: VersionRow } | null>(null);
  const [confirming, setConfirming] = useState<Version | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  /** How many times we have asked whether the first run has finished. */
  const [waiting, setWaiting] = useState(0);

  const onSignedOutRef = useRef(onSignedOut);
  onSignedOutRef.current = onSignedOut;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** The waveform's own roll — slid by the frame loop like every lane's. */
  const waveRollRef = useRef<HTMLDivElement | null>(null);
  const clockRef = useRef<HTMLSpanElement | null>(null);
  /** Lane track elements, by version id. Blocks are their ONLY children, in
   *  index order, so `children[i]` is block `i` and no per-block ref is needed.
   *  A lane that goes away is DELETED from these maps rather than set to null,
   *  so the frame loop never walks past an entry that cannot answer. */
  const trackRefs = useRef(new Map<string, HTMLDivElement>());
  /** The "what is this version saying right now" line in each lane head. */
  const nowRefs = useRef(new Map<string, HTMLDivElement>());
  const cursors = useRef(new Map<string, number>());

  /** Peaks survive switching versions and come back free; they do not survive
   *  leaving the page, which is the right lifetime for tens of kilobytes. */
  const peakCache = useRef(new Map<string, Peaks>());
  /**
   * AND THE FAILURES ARE REMEMBERED TOO, which matters more than it sounds.
   *
   * The commonest reason a decode fails is that the storage bucket sends no
   * CORS header, and that is a property of the bucket, not of the moment — so
   * retrying it every time the signed URL is refreshed means downloading the
   * whole recording again, failing again, and writing the same red line into
   * the console again, for ever. A reload is the retry.
   */
  const peakFailed = useRef(new Map<string, string>());
  const lastRefresh = useRef(0);
  const resume = useRef<{ time: number; playing: boolean } | null>(null);
  const scrubbing = useRef(false);
  /**
   * WHERE THE SCRUB STARTED — pointer x and the time it landed on. A karaoke
   * drag must be RELATIVE: the seek slides the tape under the still-held
   * pointer, so re-reading the pointer against the moving roll feeds the seek
   * back into itself and the playhead runs away ("its super fast"). Anchored,
   * a held drag moves time by hand-distance only: right pulls the tape back,
   * left winds it on, one pixel ≈ 7ms.
   */
  const scrubAnchor = useRef<{ clientX: number; t: number } | null>(null);

  /* ---- The recording picker --------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    void (async () => {
      try {
        const { recordings: rows } = await api.recordings(token, projectId, controller.signal);
        if (cancelled) return;
        setRecordings(rows);
        // A recording with no clips has no version either, so the picker opens
        // on the first one that has some rather than on an empty screen.
        if (!rows.some((r) => r.id === recordingId)) {
          const first = rows.find((r) => r.segmentCount > 0)?.id ?? rows[0]?.id ?? '';
          if (first) onRecording(first);
        }
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
        setError(err instanceof ApiFailure ? err.message : 'Could not load recordings.');
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `recordingId` is read to decide whether to pick a default, but a change
    // to it must not refetch the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, projectId, onRecording, recBump]);



  /**
   * WHICH OF THEM IS SOMEBODY WAITING ON.
   *
   * `factsKey` is bumped after a promotion and nothing else. Promoting is the
   * one act on this page that changes the answer — the version somebody was
   * waiting on becomes the truth — and a picker that still marked the recording
   * as needing review would be arguing with the button just pressed.
   */
  const [factsKey, setFactsKey] = useState(0);
  const reviewFacts = useReviewFacts(token, recordings, `${projectId}:${factsKey}`);

  /** Waiting first, then everything else, each keeping the server's
   *  newest-first order. Split rather than sorted, because a comparator over a
   *  boolean re-orders equal rows on some engines and the list would shuffle
   *  under the cursor as each lookup answered. */
  const picker = useMemo(() => {
    const rows = recordings ?? [];
    const waiting: AdminRecording[] = [];
    const rest: AdminRecording[] = [];
    for (const r of rows) (needsReview(reviewFacts.get(r.id)) ? waiting : rest).push(r);
    return { waiting, rest };
  }, [recordings, reviewFacts]);

  /* ---- The versions of this recording ------------------------------ */

  const loadVersions = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!recordingId) return;
      try {
        const fresh = await api.versions(token, recordingId, signal);
        setList(fresh);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
        setList(null);
        setError(err instanceof ApiFailure ? err.message : 'Could not load versions.');
      }
    },
    [token, recordingId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setList(null);
    setDetails({});
    setSelected([]);
    setInspect(null);
    setNotice(null);
    setPeaks(null);
    setWaveNote(null);
    setAudio(null);
    setMediaDuration(0);
    setWaiting(0);
    cursors.current.clear();
    void loadVersions(controller.signal);
    return () => controller.abort();
  }, [loadVersions]);


  /**
   * ASK AGAIN WHILE THERE IS NOTHING TO SHOW. Chained from the answer rather
   * than run on an interval, so the period is "five seconds of quiet" and two
   * requests can never be in flight at once. It stops the moment a version
   * exists, when the list fails to load (the error notice takes over), and at
   * the try limit.
   */
  useEffect(() => {
    if (!list || list.versions.length > 0 || waiting >= WAIT_TRIES) return;
    const t = window.setTimeout(() => {
      setWaiting((n) => n + 1);
      void loadVersions();
    }, WAIT_MS);
    return () => window.clearTimeout(t);
  }, [list, waiting, loadVersions]);

  /**
   * WHAT THE SCREEN OPENS ON: the truth, and the thing asking to replace it.
   *
   * The second lane is the newest SUBMITTED version when there is one — a
   * handover is somebody saying "I am done, look at this", and that is the
   * comparison an administrator came here to make. Only when nobody has
   * submitted does it fall back to the newest branch, which is the older
   * behaviour and still the right guess for a recording being worked on.
   *
   * Sorting the fallback by `createdAt` and the handover by `submittedAt` is
   * deliberate: a branch started last week and submitted this morning is the
   * newer claim on attention, and by its creation date it would lose to a
   * branch somebody opened an hour ago and abandoned.
   */
  useEffect(() => {
    if (!list) return;
    const vs = list.versions;
    if (vs.length === 0) return;
    // The versions modal can name the opening lanes: one version, or 'all'.
    if (initialLanes === 'all') {
      setSelected(vs.slice(0, MAX_LANES).map((v) => v.id));
      return;
    }
    if (initialLanes && vs.some((v) => v.id === initialLanes)) {
      setSelected([initialLanes]);
      return;
    }
    const main = vs.find((v) => v.status === 'main') ?? vs[0]!;
    const others = vs.filter((v) => v.id !== main.id);
    const second =
      newestSubmitted(others) ??
      others.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    setSelected(second ? [main.id, second.id] : [main.id]);
  }, [list, initialLanes]);

  /* ---- Each selected version's rows -------------------------------- */

  const selectedKey = selected.join(',');

  useEffect(() => {
    if (!selectedKey) return;
    const ids = selectedKey.split(',');
    const controller = new AbortController();
    let cancelled = false;

    for (const id of ids) {
      setDetails((d) => (d[id] ? d : { ...d, [id]: { at: 'loading' } }));
      void (async () => {
        try {
          const detail = await api.versionDetail(token, id, controller.signal);
          if (!cancelled) setDetails((d) => ({ ...d, [id]: { at: 'ready', detail } }));
        } catch (err) {
          if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
          if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
          setDetails((d) => ({
            ...d,
            [id]: {
              at: 'failed',
              message: err instanceof ApiFailure ? err.message : 'Could not load these clips.',
            },
          }));
        }
      })();
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, selectedKey]);

  /* ---- The audio itself -------------------------------------------- */

  const fetchAudio = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!recordingId) return;
      lastRefresh.current = Date.now();
      try {
        const fresh = await api.recordingAudio(token, recordingId, signal);
        setAudio(fresh);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
        setError(err instanceof ApiFailure ? err.message : 'Could not get a link to the audio.');
      }
    },
    [token, recordingId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchAudio(controller.signal);
    return () => controller.abort();
  }, [fetchAudio]);

  /**
   * THE SRC IS SET BY HAND, not rendered as an attribute.
   *
   * Re-rendering `<audio src={…}>` with a new signature would reload the
   * element and drop the position — which is exactly what happens when the old
   * signature expires mid-listen, the one moment the position matters most. So
   * the element is uncontrolled, and a refresh remembers where it was and puts
   * it back once the new URL has metadata.
   */
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audio) return;
    const back = resume.current;
    resume.current = null;
    el.src = audio.url;
    el.load();
    if (!back) return;
    const onReady = (): void => {
      try {
        el.currentTime = back.time;
      } catch {
        /* A browser that refuses the seek still plays from the start. */
      }
      if (back.playing) void el.play().catch(() => {});
    };
    el.addEventListener('loadedmetadata', onReady, { once: true });
    return () => el.removeEventListener('loadedmetadata', onReady);
  }, [audio]);

  /** The element's own opinion of what happened to it. */
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onMeta = (): void => setMediaDuration(Number.isFinite(el.duration) ? el.duration : 0);
    const onError = (): void => {
      /*
       * ALMOST ALWAYS THE SIGNATURE, not the file: the bytes were reachable a
       * minute ago and nothing else changed. Re-ask, remember the position,
       * and rate-limit — a media element that fails repeatedly must not turn
       * into a request loop.
       */
      if (Date.now() - lastRefresh.current < MIN_REFRESH_GAP_MS) return;
      resume.current = { time: el.currentTime, playing: !el.paused };
      void fetchAudio();
    };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('error', onError);
    };
  }, [fetchAudio]);

  /* ---- The picture of it ------------------------------------------- */

  const duration = list?.durationSec || mediaDuration || 0;

  /**
   * KARAOKE GEOMETRY — the owner's inversion: "the cursor is fixed in
   * position at the middle and the audio is the one that moves". Everything
   * time-shaped (the waveform, every lane's blocks) lives on a wide `pl-roll`
   * that the frame loop slides so the current instant always sits under the
   * fixed centre line; each lane's word floats at that same centre. The zoom
   * is fixed rather than fitted: ~140px per second, capped so the roll never
   * exceeds what a canvas can be.
   */
  const pxPerSec = duration > 0 ? Math.min(140, 30000 / duration) : 140;
  const contentW = Math.max(1, Math.round(duration * pxPerSec));

  useEffect(() => {
    if (!audio || !recordingId) return;
    const cached = peakCache.current.get(recordingId);
    if (cached) {
      setPeaks(cached);
      setWaveNote(null);
      return;
    }
    const failed = peakFailed.current.get(recordingId);
    if (failed) {
      setPeaks(null);
      setWaveNote(failed);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      try {
        const fresh = await loadPeaks(audio.url, list?.durationSec ?? 0, controller.signal);
        if (cancelled) return;
        peakCache.current.set(recordingId, fresh);
        setPeaks(fresh);
        setWaveNote(null);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        const why =
          err instanceof WaveformUnavailable ? err.message : 'The waveform could not be drawn.';
        peakFailed.current.set(recordingId, why);
        setPeaks(null);
        setWaveNote(why);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [audio, recordingId, list?.durationSec]);

  /**
   * ONE OBSERVER FOR THE GEOMETRY EVERYTHING ELSE IS MEASURED AGAINST, attached
   * BY A CALLBACK REF and not by an effect.
   *
   * The wave strip does not exist on the first render — nothing does until the
   * version list has arrived — so an effect with an empty dependency list runs
   * once against a null ref, returns, and never runs again. The observed
   * symptom is subtle and total: `trackWidth` stays 0, so the playhead computes
   * an x of zero for every timestamp and simply never moves. A callback ref
   * fires when the node actually appears, and again with null when it goes.
   */
  const observer = useRef<ResizeObserver | null>(null);
  const attachWave = useCallback((el: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const ro = new ResizeObserver(() => setTrackWidth(el.clientWidth));
    ro.observe(el);
    observer.current = ro;
    setTrackWidth(el.clientWidth);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) drawTimeline(canvas, peaks, duration);
    // `contentW` because the karaoke roll IS the canvas's width now.
  }, [peaks, duration, trackWidth, contentW]);

  /* ---- What the frame loop reads ----------------------------------- */

  const lanes = useMemo(
    () =>
      selected
        .map((id) => (list?.versions ?? []).find((v) => v.id === id))
        .filter((v): v is Version => v !== undefined),
    [selected, list],
  );

  const laneIndex = useMemo(() => {
    const m = new Map<string, LaneIndex>();
    for (const v of lanes) {
      const d = details[v.id];
      if (d?.at !== 'ready') continue;
      const rows = d.detail.segments;
      const starts = new Float64Array(rows.length);
      const ends = new Float64Array(rows.length);
      // First-heard order, not alphabetical: `speaker_2` opening the call
      // should be the top band. `rows` is already in spoken order.
      const speakers: string[] = [];
      for (let i = 0; i < rows.length; i++) {
        starts[i] = rows[i]!.startSec;
        ends[i] = rows[i]!.endSec;
        const who = rows[i]!.speaker;
        if (who && !speakers.includes(who)) speakers.push(who);
      }
      m.set(v.id, { rows, starts, ends, speakers });
    }
    return m;
  }, [lanes, details]);

  /**
   * ONE FRAME. Called from `requestAnimationFrame` and from anything that
   * moves the playhead without the clock running — a click, a resize, a lane
   * appearing.
   *
   * Everything it touches is a ref. That is the point: no state is set here, so
   * no render happens here, so the several hundred blocks per lane are laid out
   * once and then left alone for as long as somebody listens.
   */
  const paint = useCallback(
    (t: number): void => {
      // The audio moves, the cursor stands still: one translate per roll.
      const tx = trackWidth / 2 - t * pxPerSec;
      const wave = waveRollRef.current;
      if (wave) wave.style.transform = `translate3d(${tx}px,0,0)`;
      for (const roll of trackRefs.current.values()) {
        roll.style.transform = `translate3d(${tx}px,0,0)`;
      }
      if (clockRef.current) clockRef.current.textContent = clock(t);

      for (const [versionId, index] of laneIndex) {
        const track = trackRefs.current.get(versionId);
        if (!track) continue;
        const was = cursors.current.get(versionId) ?? -1;
        const now = blockAt(index.starts, index.ends, t, was);
        if (now === was) continue;
        cursors.current.set(versionId, now);

        const previous = track.children[was];
        if (previous) previous.classList.remove('is-now');
        const current = track.children[now];
        if (current) current.classList.add('is-now');

        const label = nowRefs.current.get(versionId);
        if (label) {
          const row = now >= 0 ? index.rows[now] : undefined;
          label.textContent = row ? row.heard || '—' : '—';
          label.dataset.kind = row ? row.kind : '';
        }
      }
    },
    [duration, trackWidth, laneIndex, pxPerSec],
  );

  /*
   * The loop itself is started ONCE and reads the latest `paint` through a ref.
   * Restarting an animation frame loop every time a dependency changes drops
   * frames at exactly the moments something just appeared.
   */
  const paintRef = useRef(paint);
  paintRef.current = paint;

  useEffect(() => {
    let raf = 0;
    let lastT = -1;
    const loop = (): void => {
      raf = window.requestAnimationFrame(loop);
      const el = audioRef.current;
      if (!el) return;
      const t = el.currentTime;
      if (t === lastT) return;
      lastT = t;
      paintRef.current(t);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  /**
   * WHENEVER THE PICTURE CHANGES UNDERNEATH A STILL PLAYHEAD — a lane loads, a
   * version is ticked back on, the window resizes — put it back where it
   * belongs, and FORGET WHERE EACH LANE THOUGHT IT WAS.
   *
   * The frame loop skips a lane whose block index has not changed, which is
   * what makes it cheap. But ticking a version off and on again rebuilds its
   * blocks while its cursor still says "showing block 17" — of elements that no
   * longer exist — so the loop skips it and the lane sits blank until the
   * playhead happens to cross a boundary. Clearing the cursors forces one full
   * re-apply; clearing the classes first stops the block that WAS highlighted
   * in a lane that was only resized from staying lit alongside the new one.
   */
  useEffect(() => {
    for (const track of trackRefs.current.values()) {
      track.querySelector('.pl-blk.is-now')?.classList.remove('is-now');
    }
    cursors.current.clear();
    paint(audioRef.current?.currentTime ?? 0);
  }, [paint]);

  /* ---- Moving ------------------------------------------------------ */

  const seekTo = useCallback(
    (t: number): void => {
      const el = audioRef.current;
      if (!el) return;
      const limit = duration || el.duration || 0;
      const clamped = Math.max(0, limit > 0 ? Math.min(limit - 0.001, t) : t);
      try {
        el.currentTime = clamped;
      } catch {
        /* No metadata yet. The next play starts from the beginning. */
      }
      paint(clamped);
    },
    [duration, paint],
  );

  const toggle = useCallback((): void => {
    const el = audioRef.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    // Re-ask before pressing play rather than after failing: an expiring URL
    // is knowable, and a play that dies two seconds in reads as a broken file.
    if (audio && Date.parse(audio.expiresAt) - Date.now() < REFRESH_BEFORE_EXPIRY_MS) {
      resume.current = { time: el.currentTime, playing: true };
      void fetchAudio();
      return;
    }
    void el.play().catch(() => {});
  }, [audio, fetchAudio]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (confirming) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // A focused control keeps its own keys — space on a button is that
      // button, not the transport.
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A') return;
      if (target?.isContentEditable) return;

      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        toggle();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        seekTo((audioRef.current?.currentTime ?? 0) - NUDGE_SEC);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        seekTo((audioRef.current?.currentTime ?? 0) + NUDGE_SEC);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, seekTo, confirming]);

  /** Pointer → timestamp, measured against a ROLL. The roll's rect already
   *  reflects its karaoke translation, so content-x IS the timestamp. */
  const timeFromPointer = (e: React.PointerEvent<HTMLElement>, roll: HTMLElement | null): number => {
    if (!roll || duration <= 0) return 0;
    const rect = roll.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return Math.max(0, Math.min(duration, ((e.clientX - rect.left) / rect.width) * duration));
  };

  /* ---- Clicking a lane --------------------------------------------- */

  const pickBlock = (versionId: string, e: React.PointerEvent<HTMLElement>): void => {
    const index = laneIndex.get(versionId);
    if (!index) return;
    const t = timeFromPointer(e, trackRefs.current.get(versionId) ?? null);
    const at = blockAt(index.starts, index.ends, t, cursors.current.get(versionId) ?? -1);
    const row = at >= 0 ? index.rows[at] : undefined;
    if (!row) return;
    seekTo(row.startSec);
    setInspect({ versionId, row });
  };

  /* ---- The decision ------------------------------------------------ */

  async function promote(version: Version): Promise<void> {
    setConfirming(null);
    setBusyId(version.id);
    setError(null);
    setNotice(null);
    try {
      const result = await api.promoteVersion(token, version.id);
      setList((prev) => (prev ? { ...prev, versions: result.versions } : prev));
      setNotice(result.note);
      // The picker's dots are now out of date by exactly one recording. Asking
      // again is cheap and rare — promotion is a handful of clicks a session.
      setFactsKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
      // The warn box, not the green one: a refusal rendered as a success reads
      // as though the thing worked.
      setError(err instanceof ApiFailure ? err.message : 'Could not select that version.');
    } finally {
      setBusyId(null);
    }
  }

  /* ---- Choosing lanes ---------------------------------------------- */

  function toggleLane(id: string): void {
    setSelected((prev) => {
      if (prev.includes(id)) {
        // Never down to nothing: an empty player is a page that looks broken.
        return prev.length === 1 ? prev : prev.filter((p) => p !== id);
      }
      if (prev.length >= MAX_LANES) return prev;
      // Keep the list-order (main first) so the lanes do not reshuffle.
      const order = (list?.versions ?? []).map((v) => v.id);
      return [...prev, id].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    });
  }

  const versions = list?.versions ?? [];
  const inspectVersion = inspect ? versions.find((v) => v.id === inspect.versionId) : undefined;

  return (
    <section className="card">
      <div className="card-head">
        <h2>Player</h2>
        <span className="topbar-spacer" />
      </div>
      <p className="hint">
        The recording plays once, underneath every arrangement of it. As it runs, each lane lights
        the clip it is inside — so at any instant you can see what each version calls that moment.
        <strong> Space</strong> plays and pauses, <strong>←</strong> and <strong>→</strong> move two
        seconds.
      </p>

      {error && (
        <div className="notice notice-warn" role="status">
          {error}
        </div>
      )}
      {notice && (
        <div className="notice notice-ok" role="status">
          {notice}
        </div>
      )}

      {/*
        * THE PICKER PUTS THE WAITING ONES FIRST, in real `optgroup`s rather
        * than by sorting them to the top silently. A native group has a heading
        * the browser draws itself, so "why is this one up there" is answered by
        * reading instead of by guessing — and the dot survives the collapsed
        * state of the select, where the group label does not.
        */}
      <label className="field">
        <span className="field-label">Recording</span>
        <select
          value={recordingId}
          onChange={(e) => onRecording(e.target.value)}
          disabled={!recordings || recordings.length === 0}
        >
          {recordings === null && <option value="">Loading…</option>}
          {recordings?.length === 0 && <option value="">No recordings in this project</option>}
          {picker.waiting.length > 0 ? (
            <>
              <optgroup label="Needs review">
                {picker.waiting.map((r) => (
                  <option key={r.id} value={r.id}>
                    ● {r.name} · {r.status}
                    {r.segmentCount > 0 ? ` · ${r.segmentCount} clips` : ''}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Everything else">
                {picker.rest.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} · {r.status}
                    {r.segmentCount > 0 ? ` · ${r.segmentCount} clips` : ''}
                  </option>
                ))}
              </optgroup>
            </>
          ) : (
            picker.rest.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.status}
                {r.segmentCount > 0 ? ` · ${r.segmentCount} clips` : ''}
              </option>
            ))
          )}
        </select>
        {picker.waiting.length > 0 && (
          <p className="hint-tight">
            ● somebody has handed an arrangement over and no truth has been picked for it yet.
          </p>
        )}
      </label>

      {/*
        * ONE VERSION IS NOT AN ERROR AND GETS NO CHECKBOXES. It is the normal
        * state of a freshly processed recording: the machine's first run,
        * unchallenged. Offering a picker with one option would imply something
        * is missing.
        */}
      {versions.length > 1 && (
        <div className="pl-pick" role="group" aria-label="Versions to compare">
          <span className="field-label" style={{ marginBottom: 0 }}>
            Compare
          </span>
          {versions.map((v) => {
            const on = selected.includes(v.id);
            /*
             * THE HANDOVER, WHERE THE VERSION IS NAMED. `submittedNote` is null
             * until the payload carries the stamp, so this renders nothing at
             * all today rather than an empty pill — and the WHO is already in
             * the label, which the server writes as "by Amjad Alashqar".
             */
            const note = submittedNote(v);
            return (
              <label key={v.id} className={`pl-pick-item${on ? ' is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!on && selected.length >= MAX_LANES}
                  onChange={() => toggleLane(v.id)}
                />
                <span>{v.label}</span>
                {v.status === 'main' && <span className="pill pill-done">truth so far</span>}
                {note && (
                  <span className="pill pill-submitted" title={submittedTitle(v) ?? undefined}>
                    {note}
                  </span>
                )}
              </label>
            );
          })}
          <span className="hint-tight" style={{ margin: 0 }}>
            up to {MAX_LANES} at once
          </span>
        </div>
      )}

      {(() => {
        const rec = recordings?.find((r) => r.id === recordingId);
        if (!rec || (rec.status !== 'uploaded' && rec.status !== 'failed')) return null;
        return (
          <div className="empty">
            {rec.status === 'failed'
              ? `The last run failed: ${rec.error ?? 'no reason recorded'}.`
              : 'The audio is in.'}{' '}
            Pick a pipeline and press “Generate AI run” on this audio&apos;s row in the project
            page — every run lands here as its own version.
          </div>
        );
      })()}

      {list &&
        versions.length === 0 &&
        recordings?.find((r) => r.id === recordingId)?.status !== 'uploaded' &&
        recordings?.find((r) => r.id === recordingId)?.status !== 'failed' && (
        <div className="empty">
          Nothing has been arranged for this recording yet — the pipeline is still working on it.
          {waiting < WAIT_TRIES ? (
            <div style={{ marginTop: 6 }}>This page is checking every few seconds.</div>
          ) : (
            <div style={{ marginTop: 6 }}>
              It has been a while. The Recordings tab says whether the run failed.
            </div>
          )}
          <div className="btn-row" style={{ justifyContent: 'center', marginTop: 10 }}>
            <button className="btn-quiet" type="button" onClick={() => void loadVersions()}>
              Check now
            </button>
          </div>
        </div>
      )}


      {waveNote && (
        <div className="notice notice-warn" role="status">
          {waveNote}
        </div>
      )}

      {versions.length > 0 && (
        <div className="pl-stack">
          {/* The transport, and the whole recording drawn once. */}
          <div className="pl-row">
            <div className="pl-gutter pl-transport">
              <button
                className="pl-play"
                type="button"
                onClick={toggle}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? '❙❙' : '▶'}
              </button>
              <div className="pl-time num">
                <span ref={clockRef}>00:00.0</span>
                <span className="pl-time-total"> / {clock(duration)}</span>
              </div>
            </div>
            <div
              className="pl-track pl-wavewrap"
              ref={attachWave}
              /*
                * THE SEEK HAPPENS FIRST, and the capture is allowed to fail.
                *
                * `setPointerCapture` throws when the id it is given is not an
                * active pointer, and the throw lands inside this handler —
                * taking the seek with it if the seek came second. Capture is
                * what makes a DRAG keep working past the edge of the strip; a
                * plain click must not depend on it.
                */
              onPointerDown={(e) => {
                // The click jumps ONCE, read before the tape slides; the drag
                // that may follow is relative to this anchor, never re-read
                // from the moving roll.
                const t0 = timeFromPointer(e, waveRollRef.current);
                seekTo(t0);
                scrubAnchor.current = { clientX: e.clientX, t: t0 };
                scrubbing.current = true;
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                } catch {
                  /* No capture: the click landed, the drag will not follow. */
                }
              }}
              onPointerMove={(e) => {
                const a = scrubAnchor.current;
                if (!scrubbing.current || !a) return;
                const t = a.t + (a.clientX - e.clientX) / pxPerSec;
                seekTo(Math.max(0, Math.min(duration, t)));
              }}
              onPointerUp={(e) => {
                scrubbing.current = false;
                scrubAnchor.current = null;
                try {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                } catch {
                  /* It was never captured. */
                }
              }}
              onPointerCancel={() => {
                scrubbing.current = false;
                scrubAnchor.current = null;
              }}
            >
              <div className="pl-roll" ref={waveRollRef} style={{ width: contentW }}>
                <canvas className="pl-wave" ref={canvasRef} />
              </div>
            </div>
          </div>

          {/* One lane per version. */}
          {lanes.map((v) => {
            const d = details[v.id];
            const index = laneIndex.get(v.id);
            const isMain = v.status === 'main';
            const note = submittedNote(v);
            return (
              <div className={`pl-row pl-lane${isMain ? ' is-main' : ''}`} key={v.id}>
                <div className="pl-gutter">
                  <div className="pl-lane-title">
                    {v.label}
                    {isMain && <span className="pill pill-done">truth so far</span>}
                    {note && (
                      <span className="pill pill-submitted" title={submittedTitle(v) ?? undefined}>
                        {note}
                      </span>
                    )}
                  </div>
                  <div className="pl-lane-meta num">
                    {v.authorName ?? 'the machine'} · {v.segmentCount} clips
                    {v.judgedCount !== null && v.judgedCount !== undefined
                      ? ` · ${v.judgedCount}/${v.segmentCount} settled`
                      : ''}{' '}
                    · {v.votes} {v.votes === 1 ? 'vote' : 'votes'} · {formatWhen(v.createdAt)}
                  </div>
                  {/*
                    * WHICH BAND IS WHO. Outside the roll on purpose: the frame
                    * loop finds the block under the playhead by child index, so
                    * one extra element inside the roll would highlight the
                    * wrong clip for every clip on the lane.
                    */}
                  {index && index.speakers.length > 1 && (
                    <div className="pl-speakers num">
                      {index.speakers.map((who, i) => (
                        <span key={who} className="pl-speaker">
                          <i className="pl-speaker-band">
                            <b
                              style={{
                                top: `${(i * 100) / index.speakers.length}%`,
                                height: `${100 / index.speakers.length}%`,
                              }}
                            />
                          </i>
                          {who.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  {isMain ? (
                    <button className="btn-quiet col-btn" type="button" disabled>
                      Already the truth
                    </button>
                  ) : (
                    <button
                      className="btn-quiet col-btn"
                      type="button"
                      onClick={() => setConfirming(v)}
                      disabled={busyId !== null}
                    >
                      {busyId === v.id ? 'Selecting…' : 'Select as perfect'}
                    </button>
                  )}
                </div>

                <div
                  className="pl-track pl-blocks"
                  onPointerDown={(e) => pickBlock(v.id, e)}
                  /* A BAND NEEDS ITS OWN HEIGHT. Splitting the fixed 46px
                     between two speakers leaves 23px a band, and Arabic at
                     11px in 23px is decoration rather than text. Each band
                     gets 30px and the lane grows to fit; an undiarized version
                     keeps the 46px it always had. */
                  style={
                    index && index.speakers.length > 1
                      ? { height: index.speakers.length * 30 }
                      : undefined
                  }
                >
                  <div
                    className="pl-roll"
                    style={{ width: contentW }}
                    ref={(el) => {
                      // Deleted rather than nulled: a lane that is gone must not
                      // leave an entry for the frame loop to walk past for ever.
                      if (el) trackRefs.current.set(v.id, el);
                      else trackRefs.current.delete(v.id);
                    }}
                  >
                    {index && duration > 0
                      ? index.rows.map((row) => {
                          /*
                           * ONE BAND PER SPEAKER. A clip with a speaker is drawn
                           * in that speaker's horizontal band; everything else —
                           * silence, and every row of a version nothing
                           * diarized — keeps the full height it always had.
                           *
                           * The blocks stay in `index.rows` ORDER and stay one
                           * child each: the frame loop finds the block under the
                           * playhead as `track.children[n]`, so inserting or
                           * reordering anything inside the roll would light up
                           * the wrong clip. Only `top` and `height` change.
                           */
                          const lanes = index.speakers.length;
                          const band = row.speaker ? index.speakers.indexOf(row.speaker) : -1;
                          const banded = lanes > 1 && band >= 0;
                          return (
                          <div
                            key={row.id}
                            className={`pl-blk pl-blk-${row.kind}${
                              inspect?.row.id === row.id ? ' is-picked' : ''
                            }${banded ? ' is-banded' : ''}`}
                            style={{
                              left: `${(row.startSec / duration) * 100}%`,
                              width: `${Math.max(0, (row.endSec - row.startSec) / duration) * 100}%`,
                              ...(banded
                                ? {
                                    top: `${(band * 100) / lanes}%`,
                                    height: `${100 / lanes}%`,
                                    bottom: 'auto',
                                  }
                                : {}),
                            }}
                            title={`${row.heard} · ${row.kind}${
                              row.speaker ? ` · ${row.speaker.replace(/_/g, ' ')}` : ''
                            } · ${span(row.startSec, row.endSec)}`}
                          >
                            <span className="pl-blk-txt" dir="auto" lang={list?.language}>
                              {row.heard}
                            </span>
                          </div>
                          );
                        })
                      : null}
                  </div>
                  {/*
                    * WHAT THIS VERSION IS SAYING RIGHT NOW — floating at the
                    * fixed cursor, written by the frame loop, never by React.
                    * The blocks show WHERE under the line; this shows WHAT, at
                    * a size Arabic can actually be read at, exactly where the
                    * eye already is.
                    */}
                  <div
                    className="pl-nowfloat"
                    dir="auto"
                    lang={list?.language}
                    ref={(el) => {
                      if (el) nowRefs.current.set(v.id, el);
                      else nowRefs.current.delete(v.id);
                    }}
                  >
                    —
                  </div>
                </div>

                {/* Loading and failure sit UNDER the lane rather than replacing
                    it, so one version failing to load does not move the others. */}
                {d === undefined || d.at === 'loading' ? (
                  <div className="pl-lane-note">Loading clips…</div>
                ) : d.at === 'failed' ? (
                  <div className="pl-lane-note is-bad">{d.message}</div>
                ) : null}
              </div>
            );
          })}

        </div>
      )}

      {/*
        * THE INSPECTOR. Everything the wire carries about one clip, including
        * the two numbers that are easy to confuse: `confidence` is the reader's
        * trust in the WORD, `alignScore` the aligner's trust in WHERE it sits.
        */}
      {inspect && inspectVersion && (
        <div className="pl-inspect">
          <div className="pl-inspect-head">
            <strong>{inspectVersion.label}</strong>
            <span className="pl-inspect-meta num">
              {inspectVersion.authorName ?? 'the machine'} · {inspectVersion.votes}{' '}
              {inspectVersion.votes === 1 ? 'vote' : 'votes'}
              {inspectVersion.status === 'main' ? ' · truth so far' : ''}
            </span>
            <span className="topbar-spacer" />
            <button className="btn-link" type="button" onClick={() => setInspect(null)}>
              Close
            </button>
          </div>
          <div className="pl-inspect-word" dir="auto" lang={list?.language}>
            {inspect.row.heard || <span className="vempty">(nothing)</span>}
          </div>
          {inspect.row.contextual && (
            <div className="pl-inspect-ctx" dir="auto" lang={list?.language}>
              in context: {inspect.row.contextual}
            </div>
          )}
          <dl className="pl-facts num">
            <div>
              <dt>kind</dt>
              <dd className={`vkind vkind-${inspect.row.kind}`}>{inspect.row.kind}</dd>
            </div>
            <div>
              <dt>span</dt>
              <dd>{span(inspect.row.startSec, inspect.row.endSec)}</dd>
            </div>
            <div>
              <dt>confidence</dt>
              <dd>{inspect.row.confidence === null ? '—' : inspect.row.confidence.toFixed(2)}</dd>
            </div>
            <div>
              <dt>align</dt>
              <dd>{inspect.row.alignScore === undefined ? '—' : inspect.row.alignScore.toFixed(3)}</dd>
            </div>
            <div>
              <dt>speaker</dt>
              {/* `speaker_2` is what is stored; a person reads "speaker 2".
                  An em dash means nothing separated the speakers on this run,
                  which is not the same as "one speaker". */}
              <dd>{inspect.row.speaker ? inspect.row.speaker.replace(/_/g, ' ') : '—'}</dd>
            </div>
            <div>
              <dt>verify</dt>
              <dd>{inspect.row.verify}</dd>
            </div>
          </dl>
        </div>
      )}

      {/*
        * NO `controls`, NO `src` ATTRIBUTE. The transport above is the control,
        * and the source is set by hand so an expiring signature can be replaced
        * without losing the position. See the effect that does it.
        */}
      <audio ref={audioRef} preload="metadata" />

      {confirming && (
        <ConfirmPromote
          version={confirming}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void promote(confirming)}
        />
      )}
    </section>
  );
}
